// src/hooks/useUndoRedo.js
import { useState, useCallback, useEffect } from 'react';
import { useReactFlow } from 'reactflow';

/**
 * 撤销/重做 Hook
 * 
 * 🛡️ 关键设计：undo/redo 使用函数式更新 setHistory(prev => ...)
 * 避免闭包捕获陈旧的 history 值导致快速连续操作时历史栈损坏。
 * setNodes/setEdges 作为副作用在 setHistory 回调返回新值后执行。
 */
const MAX_HISTORY = 20;

// 快照中打上 projectId，撤销/重做时只允许作用于当前图板，避免跨图板串台。
const buildSnapshot = (projectId, nodes, edges) => ({ projectId, nodes, edges });

const useUndoRedo = (projectId) => {
  const [history, setHistory] = useState({ past: [], future: [], projectId: projectId || null });
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow();

  // 图板切换时清空历史栈，防止旧图板快照污染当前图板。
  useEffect(() => {
    setHistory({ past: [], future: [], projectId: projectId || null });
  }, [projectId]);

  const resetHistory = useCallback(() => {
    setHistory({ past: [], future: [], projectId: projectId || null });
  }, [projectId]);

  const takeSnapshot = useCallback(() => {
    if (!projectId) return;
    setHistory(prev => {
      // 遇到跨图板残留状态时直接重置为当前图板历史。
      const base = prev.projectId === projectId ? prev : { past: [], future: [], projectId };
      return {
        past: [...base.past.slice(-(MAX_HISTORY - 1)), buildSnapshot(projectId, getNodes(), getEdges())],
        future: [],
        projectId,
      };
    });
  }, [projectId, getNodes, getEdges]);

  // ── undo / redo ──
  // 使用函数式更新获取最新的 history 值，避免 React 18 并发模式下的状态竞争
  const undo = useCallback(() => {
    if (!projectId) return;
    setHistory(prev => {
      // 若历史不属于当前图板，直接丢弃，防止串台。
      if (prev.projectId !== projectId) {
        return { past: [], future: [], projectId };
      }
      if (prev.past.length === 0) return prev;
      const newPast = [...prev.past];
      const targetState = newPast.pop();
      // 双重保护：目标快照 projectId 不匹配时不应用。
      if (targetState?.projectId !== projectId) {
        return { past: [], future: [], projectId };
      }
      const present = buildSnapshot(projectId, getNodes(), getEdges());
      // 副作用：恢复节点和边状态
      setNodes(targetState.nodes);
      setEdges(targetState.edges);
      return { past: newPast, future: [present, ...prev.future], projectId };
    });
  }, [projectId, getNodes, getEdges, setNodes, setEdges]);

  const redo = useCallback(() => {
    if (!projectId) return;
    setHistory(prev => {
      if (prev.projectId !== projectId) {
        return { past: [], future: [], projectId };
      }
      if (prev.future.length === 0) return prev;
      const newFuture = [...prev.future];
      const targetState = newFuture.shift();
      if (targetState?.projectId !== projectId) {
        return { past: [], future: [], projectId };
      }
      const present = buildSnapshot(projectId, getNodes(), getEdges());
      // 副作用：恢复节点和边状态
      setNodes(targetState.nodes);
      setEdges(targetState.edges);
      return { past: [...prev.past, present], future: newFuture, projectId };
    });
  }, [projectId, getNodes, getEdges, setNodes, setEdges]);

  return {
    takeSnapshot,
    undo,
    redo,
    resetHistory,
    canUndo: history.projectId === (projectId || null) && history.past.length > 0,
    canRedo: history.projectId === (projectId || null) && history.future.length > 0,
  };
};

export default useUndoRedo;
